import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";

export type AccessibleUser = {
  guid: string;
  name?: string;
  mail: string;
};

const accessibleUsersApi = new API<AccessibleUser>("accessibleUser");

/**
 * React Query hook for fetching accessible users
 */
export function useAccessibleUsers() {
  return useQuery({
    queryKey: queryKeys.accessibleUsers.all,
    queryFn: () => {
      // The API doesn't have a getAll method that we can use directly
      // This hook will be populated from the cache when individual users are fetched
      return [] as AccessibleUser[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * React Query hook for fetching a single accessible user by ID
 */
export function useAccessibleUser(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.accessibleUsers.detail(userId ?? ""),
    queryFn: async () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return await accessibleUsersApi.getOne(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 60, // 1 hour - user info doesn't change often
  });
}

/**
 * React Query hook for getting all accessible users from cache.
 * Note: This relies on users being fetched via useAccessibleUser first.
 */
export function useAllAccessibleUsers(): {
  data: AccessibleUser[];
  isLoading: boolean;
} {
  const queryClient = useQueryClient();

  // Get all cached accessible users from React Query
  const queryCache = queryClient.getQueryCache();
  const accessibleUserQueries = queryCache
    .getAll()
    .filter(
      (query) =>
        query.queryKey[0] === "accessibleUser" &&
        typeof query.queryKey[1] === "string" &&
        query.state.data
    );

  const queryUsers = accessibleUserQueries
    .map((query) => query.state.data as AccessibleUser)
    .filter(Boolean);

  return {
    data: queryUsers,
    isLoading: false,
  };
}

/**
 * Fetch accessible user by ID directly (not a hook, for use in services).
 */
export async function fetchAccessibleUserByIdQuery(
  userId: string
): Promise<AccessibleUser> {
  return accessibleUsersApi.getOne(userId);
}
